import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useTranslations } from '@core/js/hooks/i18n';

import { useGetGeocode } from '@interlayer/js/app/hooks/postCodeFinder/hooks/useGeocode';
import { useGetClubsList } from '@interlayer/js/app/hooks/postCodeFinder/hooks/useGetClubsList';
import { getDistance } from '@interlayer/js/app/hooks/postCodeFinder/utils/getDistance';
import { usePostCodeSelector } from '@interlayer/js/app/store/hooks/usePostCodeSelector';
import { setIsLoadingNearestClub } from '@interlayer/js/app/store/reducers/postCodeFinder/actions';
import { TClubsListData, TClubsListItem } from '@interlayer/js/app/store/reducers/postCodeFinder/types';

export const useNearestClub = () => {
    const fetchClubList = useGetClubsList();
    const dispatch = useDispatch();
    const { fetch: getGeoByQuery } = useGetGeocode();
    const t = useTranslations('playtech.mobile.system.registration.');
    const { clubsList, isClubListLoaded, isLoading: isLoadingNearestClub } = usePostCodeSelector();
    const [clubsNearest, setClubsNearest] = useState<TClubsListItem[]>([]);

    const getNearestClubs = useCallback(async (
        zip?: string | string[],
        isBrowserGeo?: boolean,
        clubData?: TClubsListData[]
    ): Promise<TClubsListItem[] | undefined> => {
        if (isLoadingNearestClub) {
            return;
        }

        if (!zip) {
            return Promise.reject(t('validation.zip-notFound'));
        }

        if (!isClubListLoaded && !clubData) {
            return fetchClubList()
                .then((response) => getNearestClubs(zip, isBrowserGeo, response));
        }

        dispatch(setIsLoadingNearestClub(true));

        const clubList = clubData?.length ? clubData : clubsList;

        const [Latitude, Longitude] = isBrowserGeo && Array.isArray(zip) ? zip : await getGeoByQuery({ paramsFetch: Array.isArray(zip) ? zip.join(',') : zip });

        const data = !Latitude || !Longitude ? clubList : clubList.map((club: TClubsListData) => ({
            ...club,
            Distance: getDistance(Latitude, Longitude, club.Latitude, club.Longitude)
        })).sort((a, b) => a.Distance - b.Distance);

        setClubsNearest(data);

        dispatch(setIsLoadingNearestClub(false));

        return Promise.resolve(data);
    }, [clubsList, dispatch, fetchClubList, getGeoByQuery, isClubListLoaded, isLoadingNearestClub, t]);

    return useMemo(() => ({ clubsNearest, getNearestClubs }), [clubsNearest, getNearestClubs]);
};
